import React from 'react';
import { Bell, AlertTriangle, FileText } from 'lucide-react';
import { FinancialTransaction } from '../../services/database/supabase';
import { MonthlyReport } from './MonthlyReport';

interface FinancialAlertsProps {
  transactions: FinancialTransaction[];
  isBlurred: boolean;
}

export const FinancialAlerts: React.FC<FinancialAlertsProps> = ({
  transactions,
  isBlurred
}) => {
  // Encontrar o próximo pagamento
  const getNextPayment = () => {
    const today = new Date();
    const futureTransactions = transactions
      .filter(t => {
        const transactionDate = new Date(t.date.split('T')[0]);
        return transactionDate > today && t.type === 'Despesas';
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    return futureTransactions[0];
  };

  // Gerar relatório mensal
  const generateMonthlyReport = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.date.split('T')[0]);
      return transactionDate.getMonth() === currentMonth && 
             transactionDate.getFullYear() === currentYear;
    });

    const totalIncome = monthTransactions
      .filter(t => t.type === 'Receita' || t.type === 'Salário')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = monthTransactions
      .filter(t => t.type === 'Despesas')
      .reduce((sum, t) => sum + t.amount, 0);

    const balance = totalIncome - totalExpenses;
    const savings = (balance / totalIncome) * 100;

    return {
      totalIncome,
      totalExpenses,
      balance,
      savings: isNaN(savings) ? 0 : savings,
      transactionCount: monthTransactions.length
    };
  };

  // Verificar gastos altos
  const checkHighExpenses = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthExpenses = transactions.filter(t => {
      const transactionDate = new Date(t.date.split('T')[0]);
      return transactionDate.getMonth() === currentMonth && 
             transactionDate.getFullYear() === currentYear &&
             t.type === 'Despesas';
    });

    // Calcular média de gastos
    const averageExpense = monthExpenses.reduce((sum, t) => sum + t.amount, 0) / monthExpenses.length;
    
    // Encontrar gastos 50% acima da média
    return monthExpenses.filter(t => t.amount > averageExpense * 1.5);
  };

  const nextPayment = getNextPayment();
  const highExpenses = checkHighExpenses();

  return (
    <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08] space-y-6">
      <h3 className="text-lg font-semibold">Análise Financeira</h3>
      
      {/* Próximo Pagamento */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Bell className="text-blue-500" size={20} />
          </div>
          <h3 className="font-medium">Próximo Pagamento</h3>
        </div>
        {nextPayment ? (
          <div>
            <p className="text-lg font-semibold">
              {isBlurred ? '****' : `R$ ${nextPayment.amount.toFixed(2)}`}
            </p>
            <p className="text-sm text-white/60">
              {nextPayment.description} - {(() => {
                const [year, month, day] = nextPayment.date.split('T')[0].split('-');
                return `${day}/${month}/${year}`;
              })()}
            </p>
          </div>
        ) : (
          <p className="text-sm text-white/60">Nenhum pagamento agendado</p>
        )}
      </div>

      {/* Relatório Mensal */}
      <MonthlyReport
        transactions={transactions}
        isBlurred={isBlurred}
      />

      {/* Alertas de Gastos */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <AlertTriangle className="text-orange-500" size={20} />
          </div>
          <h3 className="font-medium">Alertas de Gastos</h3>
        </div>
        {highExpenses.length > 0 ? (
          <div className="space-y-2">
            {highExpenses.map((expense, index) => (
              <div key={index} className="text-sm">
                <p className="font-medium">{expense.description}</p>
                <p className="text-white/60">
                  {isBlurred ? '****' : `R$ ${expense.amount.toFixed(2)}`}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/60">Nenhum gasto alto detectado</p>
        )}
      </div>
    </div>
  );
};
